import type { MouseEvent } from 'react';
import {
    StackIcon,
    ClockIcon,
    PencilIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Badge, type BadgeVariant, Card } from '@/components';
import { EntityActionsMenu } from '@/components/EntityActionsMenu';
import type { ActionMenuItem } from '@/components/EntityActionsMenu';
import { Thumbnail } from '@/components/Thumbnail';
import Config from '@/config';
import { formatRelativeTime } from '@/utils/date';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ReportStatus, ReportType, type ReportListItem } from '@/types/report';

interface ReportCardProps {
    report: ReportListItem;
    checked?: boolean;
    isMultiSelectMode: boolean;
    onClick: (reportId: string, event?: MouseEvent<HTMLDivElement>) => void;
    onDelete: (reportId: string) => void;
    onEdit: () => void;
}

const EMPTY_PLACEHOLDER = <></>;

function previewThumbnailNeedsAuth(src: string | undefined): boolean {
    return src ? src.startsWith(Config.VITE_ENGINE_URL) : false;
}

function reportStatusBadgeVariant(status: ReportStatus): BadgeVariant {
    switch (status) {
        case ReportStatus.COMPLETED:
            return 'positive-glass';
        case ReportStatus.ERROR:
            return 'error';
        case ReportStatus.PENDING:
        case ReportStatus.PROCESSING:
            return 'analyzing';
        default: {
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

function reportStatusLabel(status: ReportStatus, translate: TFunction): string {
    switch (status) {
        case ReportStatus.PENDING:
            return translate('filters.statusPending');
        case ReportStatus.PROCESSING:
            return translate('filters.statusProcessing');
        case ReportStatus.COMPLETED:
            return translate('filters.statusCompleted');
        case ReportStatus.ERROR:
            return translate('filters.statusError');
        default: {
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

function buildReportCardMenuItems({
    editLabel,
    deleteLabel,
    onEdit,
    onDelete,
}: {
    editLabel: string;
    deleteLabel: string;
    onEdit: () => void;
    onDelete: () => void;
}): ActionMenuItem[] {
    return [
        {
            key: 'edit',
            label: editLabel,
            icon: PencilIcon,
            onSelect: onEdit,
        },
        {
            key: 'delete',
            label: deleteLabel,
            icon: TrashIcon,
            variant: 'danger',
            onSelect: onDelete,
        },
    ];
}

function ReportPreviewThumbnail({
    src,
    className,
}: {
    src: string | undefined;
    className?: string;
}) {
    return (
        <div className={`relative bg-grey-700 min-h-0 ${className ?? ''}`}>
            <Thumbnail
                size="fill"
                imageSrc={src}
                imageUsesAuth={previewThumbnailNeedsAuth(src)}
                placeholderIcon={EMPTY_PLACEHOLDER}
                className="rounded-none"
            />
        </div>
    );
}

const PREVIEW_COLLAGE_THUMBNAIL_COUNT = 3;

function ReportPreviewCollage({ thumbnails }: { thumbnails: string[] }) {
    const hasSingleThumbnail =
        thumbnails.length > 0 &&
        thumbnails.length < PREVIEW_COLLAGE_THUMBNAIL_COUNT;

    if (hasSingleThumbnail) {
        return (
            <ReportPreviewThumbnail src={thumbnails[0]} className="h-full" />
        );
    }

    return (
        <div className="grid grid-cols-[13fr_7fr] grid-rows-2 gap-1 h-full w-full bg-grey-600">
            <ReportPreviewThumbnail
                src={thumbnails[0]}
                className="row-span-2"
            />
            <ReportPreviewThumbnail src={thumbnails[1]} />
            <ReportPreviewThumbnail src={thumbnails[2]} />
        </div>
    );
}

export function ReportCard({
    report,
    checked,
    isMultiSelectMode,
    onClick,
    onDelete,
    onEdit,
}: ReportCardProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const previewThumbnails = (report.previewThumbnails ?? []).filter(
        (thumbnail): thumbnail is string => Boolean(thumbnail),
    );

    const metadata = [
        {
            icon: StackIcon,
            text: t('card.events', { count: report.eventCount }),
        },
        {
            icon: ClockIcon,
            text: t('card.lastUpdated', {
                time: formatRelativeTime(report.updatedAt),
            }),
            textClassName: 'lowercase first-letter:uppercase',
        },
    ];

    return (
        <div data-testid="report-card" className="h-full">
            <Card
                title={report.name}
                thumbnailMedia={
                    <ReportPreviewCollage thumbnails={previewThumbnails} />
                }
                topLeftSlot={
                    report.status !== ReportStatus.COMPLETED ||
                    report.type === ReportType.PROCEDURE ? (
                        <Badge
                            value={reportStatusLabel(report.status, t)}
                            variant={reportStatusBadgeVariant(report.status)}
                        />
                    ) : undefined
                }
                metadata={metadata}
                selectable={isMultiSelectMode}
                checked={checked ?? false}
                sideAction={
                    !isMultiSelectMode ? (
                        <EntityActionsMenu
                            triggerTestId="report-card-actions-menu"
                            items={buildReportCardMenuItems({
                                editLabel: t('card.menuEdit'),
                                deleteLabel: t('card.menuDelete'),
                                onEdit,
                                onDelete: () => onDelete(report.id),
                            })}
                        />
                    ) : undefined
                }
                onClick={(event) => onClick(report.id, event)}
            />
        </div>
    );
}
